import React, { useState } from 'react';

import { Route, Routes, useNavigate, Navigate } from 'react-router-dom';

import ErrorPage from '@/components/errors/ErrorPage';
import AccountRegistrationConfirmationPage from '@/features/registration/components/AccountRegistrationConfirmationPage';
import AccountRegistrationEmailSendPage from '@/features/registration/components/AccountRegistrationEmailSendPage';
import AccountRegistrationPage from '@/features/registration/components/AccountRegistrationPage';
import AccountRegistrationVerifyPage from '@/features/registration/components/AccountRegistrationVerifyPage';
import useRequestRegistrationTokens from '@/hooks/registration/useRequestRegistrationTokens';
import {
  type AccountRegistrationFormValues,
  type StudentInfo,
} from '@/interfaces/registration';

interface RegistrationState {
  formValues: AccountRegistrationFormValues;
  studentInfo: StudentInfo | null;
  resendErrorStatus?: number;
}

const AccountRegistration: React.FC = () => {
  const [registrationState, setRegistrationState] = useState<RegistrationState>(
    {
      formValues: {
        password: '',
        password_confirmation: '',
        last_name: '',
        first_name: '',
        last_name_kana: '',
        first_name_kana: '',
        email: '',
        code: '',
        student_first_name_kana: '',
        student_last_name_kana: '',
      },
      studentInfo: null,
      resendErrorStatus: undefined,
    },
  );
  const [parentEmailRegistrationTokenId, setParentEmailRegistrationTokenId] =
    useState<string | undefined>();

  const navigate = useNavigate();

  const clearOnlyPasswordsFormValues = (
    formValues: AccountRegistrationFormValues,
  ) => {
    setRegistrationState((prev) => ({
      ...prev,
      formValues: {
        last_name: formValues.last_name,
        first_name: formValues.first_name,
        last_name_kana: formValues.last_name_kana,
        first_name_kana: formValues.first_name_kana,
        email: formValues.email,
        code: formValues.code,
        student_first_name_kana: formValues.student_first_name_kana,
        student_last_name_kana: formValues.student_last_name_kana,
        password: '',
        password_confirmation: '',
      },
    }));
  };

  const onSubmit = (
    currentFormValues: AccountRegistrationFormValues,
    studentInfo: StudentInfo,
  ) => {
    setRegistrationState({
      formValues: currentFormValues,
      studentInfo: studentInfo,
      resendErrorStatus: undefined,
    });
    navigate('/registration/confirm');
  };

  const onBack = () => {
    // 戻るボタンを押したら、全てのフォーム情報をクリアしてフォーム画面に戻る
    clearOnlyPasswordsFormValues(registrationState.formValues);
    navigate('/registration');
  };

  const onClickAccountRegistration = (data?: {
    parent_email_registration_token_id: string;
  }) => {
    if (data?.parent_email_registration_token_id) {
      setParentEmailRegistrationTokenId(
        data.parent_email_registration_token_id,
      );
    }
    navigate('/registration/email_sent');
  };

  const { sendRegistrationToken, isPending } = useRequestRegistrationTokens();
  const onResend = () => {
    // メール送信完了画面で再送信ボタン押下時: POST処理実行後にメール送信完了画面を再表示
    const { password_confirmation: _, ...requestBody } =
      registrationState.formValues;
    sendRegistrationToken(
      requestBody,
      (data) => {
        if (data?.parent_email_registration_token_id) {
          setParentEmailRegistrationTokenId(
            data.parent_email_registration_token_id,
          );
        }
        navigate('/registration/email_sent');
      },
      (error) => {
        setRegistrationState((prev) => ({
          ...prev,
          resendErrorStatus: error.status,
        }));
        navigate('/registration/confirm');
      },
      parentEmailRegistrationTokenId,
    );
  };

  return (
    <Routes>
      <Route
        path=""
        element={
          <AccountRegistrationPage
            onSubmit={onSubmit}
            formValues={registrationState.formValues}
          />
        }
      />
      <Route
        path="/confirm"
        element={
          registrationState.studentInfo ? (
            <AccountRegistrationConfirmationPage
              onBack={onBack}
              onClickAccountRegistration={onClickAccountRegistration}
              formValues={registrationState.formValues}
              studentInfo={registrationState.studentInfo}
              resendErrorStatus={registrationState.resendErrorStatus}
            />
          ) : (
            <Navigate to="/registration" replace />
          )
        }
      />
      <Route
        path="/email_sent"
        element={
          <AccountRegistrationEmailSendPage
            email={registrationState.formValues.email}
            isFetching={isPending}
            onResend={onResend}
            onGoBack={onBack}
          />
        }
      />
      <Route path="/verify" element={<AccountRegistrationVerifyPage />} />
      <Route path="*" element={<ErrorPage type="not_found" />} />
    </Routes>
  );
};

export default AccountRegistration;
